/**
 * HR / Leave Packages — templates for HR to create once and bulk-apply to
 * employees. Each package holds the per-type quotas (sick/personal/vacation),
 * whether to prorate based on join-date, and the year-end carry-over cap.
 *
 * Flow:
 *   1. HR builds a package (e.g. "พนักงานประจำ").
 *   2. Open the package → multi-select employees in the assign panel → apply.
 *      Each selected employee gets a leave_quota row for the chosen year with
 *      values from the package × prorate_factor (when prorate_on_join=true).
 *   3. Year-end: HR clicks "ยกยอดปีหน้า" → rollover endpoint reads unused
 *      vacation across all packaged employees and seeds next year's
 *      carried_days (capped per package.carry_over_max).
 *
 * Hour↔day toggle in the package editor: stored as fractional days, but HR
 * can type in hours and the input converts. tenant `WORK_HOURS_PER_DAY`
 * setting drives the conversion (default 8).
 */
import { useState } from 'react';
import { Plus, Trash2, Save, X, Users, UserPlus, ArrowRightLeft, Check } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import {
  useLeavePackages, useUpsertLeavePackage, useDeleteLeavePackage,
  useAssignLeavePackage, useRolloverLeave, useEmployees, useQuotas,
  usePackageMembers,
  type LeavePackage,
} from '../../hooks';

const HOURS_PER_DAY = 8;

function daysToHours(d: number): number { return Math.round(d * HOURS_PER_DAY * 100) / 100; }
function hoursToDays(h: number): number { return Math.round((h / HOURS_PER_DAY) * 100) / 100; }

/** Content-only version used by the Time hub. */
export function HRLeavePackagesContent() {
  const { data: pkgs, isLoading, error, refetch } = useLeavePackages();
  const upsert = useUpsertLeavePackage();
  const del = useDeleteLeavePackage();
  const rollover = useRolloverLeave();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Partial<LeavePackage> | null>(null);
  const [assignFor, setAssignFor] = useState<LeavePackage | null>(null);
  const [membersFor, setMembersFor] = useState<LeavePackage | null>(null);

  if (isLoading) return <Loading />;
  if (error || !pkgs) return <LoadError error={error} onRetry={() => refetch()} />;

  async function handleSave(pkg: Partial<LeavePackage> & { name: string }) {
    try {
      await upsert.mutateAsync(pkg);
      toast.success(pkg.id ? 'อัปเดตแพคเกจแล้ว' : 'สร้างแพคเกจใหม่แล้ว');
      setEditing(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function handleDelete(p: LeavePackage) {
    if (!(await confirm({ title: 'ลบแพคเกจ?', message: `"${p.name}" — ข้อมูล leave_quota เดิมจะถูกตัดออกจากแพคเกจ แต่ค่าทั้งหมดจะถูกเก็บไว้ (ไม่ลบ)` }))) return;
    try {
      await del.mutateAsync(p.id);
      toast.success('ลบแพคเกจแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    }
  }

  async function handleRollover() {
    const fromYear = new Date().getFullYear();
    if (!(await confirm({
      title: `ยกยอดวันลาจากปี ${fromYear + 543} → ${fromYear + 544}?`,
      message: 'ระบบจะคำนวณวันลาพักร้อนเหลือของพนักงานในแพคเกจ (ที่ตั้ง carry_over_max > 0) แล้วเติมเข้าปีถัดไปเป็น "วันยกยอด"',
    }))) return;
    try {
      const res = await rollover.mutateAsync(fromYear);
      toast.success(`ยกยอดสำเร็จ ${res.rolled} คน`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ยกยอดไม่สำเร็จ');
    }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-sm text-gray-600">กำหนดสิทธิ์การลา + วันยกยอด สำหรับพนักงานแต่ละกลุ่ม</p>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={handleRollover} disabled={rollover.isPending} className="btn-secondary text-xs">
            <ArrowRightLeft className="w-4 h-4" /> ยกยอดปีหน้า
          </button>
          <button onClick={() => setEditing({ name: '', sick_quota: 30, personal_quota: 3, vacation_quota: 6, prorate_on_join: true, carry_over_max: 0 })} className="btn-primary text-xs">
            <Plus className="w-4 h-4" /> สร้างแพคเกจใหม่
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        แพคเกจคือ "ชุดสิทธิ์การลา" ที่ตั้งไว้ครั้งเดียว — สร้างแล้วเอาไปใส่ให้พนักงานเป็นกลุ่มได้ทันที.
        พนักงานเข้ากลางปี ระบบ prorate ให้อัตโนมัติ; วันลาเหลือเอาไปใช้ปีหน้าได้ตามที่ตั้ง.
      </p>

      {pkgs.length === 0 ? (
        <div className="card p-6 text-center text-sm text-gray-500">
          ยังไม่มีแพคเกจ — กด "สร้างแพคเกจใหม่" ด้านบนเพื่อเริ่ม
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pkgs.map((p) => (
            <div key={p.id} className="card p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {p.name}
                    <span className={`text-xs font-normal px-1.5 py-0.5 rounded-full shrink-0 ${p.member_count > 0 ? 'bg-navy/10 text-navy' : 'bg-gray-100 text-gray-400'}`}>
                      {p.member_count} คน
                    </span>
                  </div>
                  {p.description && <div className="text-xs text-gray-500 truncate">{p.description}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing(p)} className="text-xs px-2 py-1 hover:bg-gray-100 rounded">แก้</button>
                  <button onClick={() => handleDelete(p)} className="text-xs px-2 py-1 hover:bg-red-50 text-red-600 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <Stat label="ลาป่วย" days={p.sick_quota} />
                <Stat label="ลากิจ" days={p.personal_quota} />
                <Stat label="พักร้อน" days={p.vacation_quota} />
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600 pt-1">
                {p.prorate_on_join && <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">เข้ากลางปี → prorate</span>}
                {p.carry_over_max > 0 && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">ยกยอดได้ {p.carry_over_max} วัน</span>}
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setMembersFor(p)}
                  disabled={p.member_count === 0}
                  className="btn-secondary flex-1 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Users className="w-4 h-4" /> {p.member_count > 0 ? 'ดูรายชื่อ' : 'ยังไม่มีใครใช้'}
                </button>
                <button onClick={() => setAssignFor(p)} className="btn-primary flex-1 text-sm">
                  <UserPlus className="w-4 h-4" /> ใส่ให้พนักงาน
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PackageEditor
          initial={editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          saving={upsert.isPending}
        />
      )}
      {assignFor && (
        <AssignPanel pkg={assignFor} onClose={() => setAssignFor(null)} />
      )}
      {membersFor && (
        <MembersModal
          pkg={membersFor}
          onClose={() => setMembersFor(null)}
          onReassign={() => { setAssignFor(membersFor); setMembersFor(null); }}
        />
      )}
    </>
  );
}

/** รายชื่อพนักงานที่ใช้แพคเกจนี้ (ปีปัจจุบัน) — กดจากการ์ดแพคเกจ เผื่อย้าย/ปรับ */
function MembersModal({ pkg, onClose, onReassign }: { pkg: LeavePackage; onClose: () => void; onReassign: () => void }) {
  const year = new Date().getFullYear();
  const { data, isLoading } = usePackageMembers(pkg.id, year);
  const members = data?.members ?? [];

  // จัดกลุ่มตามแผนกเพื่อให้เห็นภาพง่ายเวลาย้าย
  const byDept = new Map<string, typeof members>();
  for (const m of members) {
    const d = m.department || 'ไม่ระบุแผนก';
    if (!byDept.has(d)) byDept.set(d, []);
    byDept.get(d)!.push(m);
  }

  return (
    <Modal open onClose={onClose} title={`รายชื่อในแพคเกจ "${pkg.name}"`} size="max-w-lg">
      <div className="space-y-3">
        <p className="text-sm text-gray-500">ปี {year + 543} · {members.length} คน</p>

        {isLoading ? (
          <Loading />
        ) : members.length === 0 ? (
          <div className="card p-6 text-center text-sm text-gray-500">ยังไม่มีพนักงานในแพคเกจนี้</div>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto space-y-3 pr-1">
            {[...byDept.entries()].map(([dept, list]) => (
              <div key={dept}>
                <div className="text-xs font-medium text-gray-400 mb-1">{dept} · {list.length} คน</div>
                <div className="space-y-1">
                  {list.map((m) => (
                    <div key={m.emp_code} className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{m.name}</div>
                        <div className="text-xs text-gray-500 truncate">{m.emp_code}</div>
                      </div>
                      <div className="text-xs text-right shrink-0 text-gray-600">
                        <div>พักร้อน {m.vacation_quota} วัน{m.carried_days > 0 ? ` (+ยกยอด ${m.carried_days})` : ''}</div>
                        {m.prorate_factor < 1 && <div className="text-amber-600">prorate {Math.round(m.prorate_factor * 100)}%</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 pt-1 border-t border-gray-100">
          <button onClick={onClose} className="btn-secondary flex-1"><X className="w-4 h-4" /> ปิด</button>
          <button onClick={onReassign} className="btn-primary flex-1"><UserPlus className="w-4 h-4" /> เพิ่ม / ย้ายคนเข้าแพคเกจนี้</button>
        </div>
      </div>
    </Modal>
  );
}

/** Standalone page kept for legacy /hr/leave-packages (now redirects via Time hub). */
export function HRLeavePackages() {
  return (
    <AdminLayout title="แพคเกจสิทธิ์การลา">
      <HRLeavePackagesContent />
    </AdminLayout>
  );
}

function Stat({ label, days }: { label: string; days: number }) {
  return (
    <div className="bg-gray-50 rounded p-1.5 text-center">
      <div className="text-[10px] text-gray-500">{label}</div>
      <div className="font-semibold text-sm">{days} วัน</div>
      <div className="text-[10px] text-gray-400">{daysToHours(days)} ชม.</div>
    </div>
  );
}

function PackageEditor({ initial, onSave, onCancel, saving }: {
  initial: Partial<LeavePackage>;
  onSave: (p: Partial<LeavePackage> & { name: string }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name ?? '',
    description: initial.description ?? '',
    sick_quota: initial.sick_quota ?? 30,
    personal_quota: initial.personal_quota ?? 3,
    vacation_quota: initial.vacation_quota ?? 6,
    prorate_on_join: initial.prorate_on_join ?? true,
    carry_over_max: initial.carry_over_max ?? 0,
    vacation_first_year_eligible: initial.vacation_first_year_eligible ?? false,
  });
  const [unit, setUnit] = useState<'day' | 'hour'>('day');

  function setQuota(key: 'sick_quota' | 'personal_quota' | 'vacation_quota' | 'carry_over_max', raw: string) {
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    setForm((p) => ({ ...p, [key]: unit === 'hour' ? hoursToDays(num) : num }));
  }
  const display = (v: number) => unit === 'hour' ? daysToHours(v) : v;

  return (
    <Modal open onClose={onCancel} title={form.id ? 'แก้ไขแพคเกจ' : 'สร้างแพคเกจใหม่'}>
      <div className="space-y-3">
        <div>
          <label className="label">ชื่อแพคเกจ</label>
          <input className="input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="เช่น พนักงานประจำ / พาร์ทไทม์ / ฝึกงาน" />
        </div>
        <div>
          <label className="label">คำอธิบาย (ไม่จำเป็น)</label>
          <input className="input" value={form.description ?? ''} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-medium">หน่วยที่กรอก</span>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
            <button onClick={() => setUnit('day')} className={`px-3 py-1.5 ${unit === 'day' ? 'bg-brand-600 text-white' : 'bg-white'}`}>วัน</button>
            <button onClick={() => setUnit('hour')} className={`px-3 py-1.5 ${unit === 'hour' ? 'bg-brand-600 text-white' : 'bg-white'}`}>ชั่วโมง (×8)</button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <NumField label="ลาป่วย" suffix={unit === 'hour' ? 'ชม.' : 'วัน'} value={display(form.sick_quota)} onChange={(v) => setQuota('sick_quota', v)} />
          <NumField label="ลากิจ" suffix={unit === 'hour' ? 'ชม.' : 'วัน'} value={display(form.personal_quota)} onChange={(v) => setQuota('personal_quota', v)} />
          <NumField label="พักร้อน" suffix={unit === 'hour' ? 'ชม.' : 'วัน'} value={display(form.vacation_quota)} onChange={(v) => setQuota('vacation_quota', v)} />
        </div>

        <label className="flex items-center gap-2 pt-2 cursor-pointer">
          <input type="checkbox" checked={form.prorate_on_join} onChange={(e) => setForm((p) => ({ ...p, prorate_on_join: e.target.checked }))} />
          <span className="text-sm">เข้ากลางปี → prorate ตามวันที่เริ่มงาน</span>
        </label>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.vacation_first_year_eligible}
            onChange={(e) => setForm((p) => ({ ...p, vacation_first_year_eligible: e.target.checked }))}
          />
          <span className="text-sm">
            ใช้สิทธิ์พักร้อนได้ตั้งแต่ปีแรก
            <span className="block text-xs text-gray-500 mt-0.5">
              ถ้าไม่ติ๊ก: กฎไทย §30 — ได้สิทธิ์พักร้อนในปีที่ทำงานครบ 1 ปี (ครบปีกลางปีก็ได้เต็มสิทธิ์ของปีนั้น · คนที่ยังไม่ครบปีภายในสิ้นปีจะเป็น 0) · ติ๊ก: กรณีพิเศษ ให้ใช้ได้ตั้งแต่วันแรก
            </span>
          </span>
        </label>

        <div>
          <label className="label">ยกยอดพักร้อนปีหน้า (สูงสุดกี่ {unit === 'hour' ? 'ชั่วโมง' : 'วัน'})</label>
          <NumField label="" suffix={unit === 'hour' ? 'ชม.' : 'วัน'} value={display(form.carry_over_max)} onChange={(v) => setQuota('carry_over_max', v)} />
          <p className="text-xs text-gray-500 mt-1">0 = ปิดการยกยอด · พักร้อนที่ไม่ใช้จะหมดสิ้นปี</p>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="btn-secondary flex-1"><X className="w-4 h-4" /> ยกเลิก</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name.trim()}
            className="btn-primary flex-1"
          >
            <Save className="w-4 h-4" /> {saving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function NumField({ label, suffix, value, onChange }: { label: string; suffix: string; value: number; onChange: (v: string) => void }) {
  return (
    <div>
      {label && <label className="label">{label}</label>}
      <div className="relative">
        <input
          type="number"
          step="0.5"
          min="0"
          className="input pr-10"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>
      </div>
    </div>
  );
}

function AssignPanel({ pkg, onClose }: { pkg: LeavePackage; onClose: () => void }) {
  const { data: employees } = useEmployees();
  const { data: quotas } = useQuotas(new Date().getFullYear());
  const assign = useAssignLeavePackage();
  const toast = useToast();

  const [year, setYear] = useState(new Date().getFullYear());
  const [applyProrate, setApplyProrate] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const empList = ((employees ?? []) as Array<{ emp_code: string; first_name: string; last_name: string; department: string | null; start_date: string | null }>);
  const quotaByEmp = new Map((quotas ?? []).map((q) => [q.emp_code, q]));
  const filtered = empList.filter((e) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return e.emp_code.toLowerCase().includes(q) ||
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.department ?? '').toLowerCase().includes(q);
  });

  function toggle(emp: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(emp)) next.delete(emp); else next.add(emp);
      return next;
    });
  }
  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((e) => e.emp_code)));
  }

  async function handleApply() {
    if (selected.size === 0) { toast.error('เลือกพนักงานอย่างน้อย 1 คน'); return; }
    try {
      const res = await assign.mutateAsync({
        package_id: pkg.id,
        year,
        emp_codes: Array.from(selected),
        apply_prorate: applyProrate,
      });
      toast.success(`ใส่แพคเกจ "${pkg.name}" ให้ ${res.assigned} คนแล้ว`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ใส่แพคเกจไม่สำเร็จ');
    }
  }

  return (
    <Modal open onClose={onClose} title={`ใส่ "${pkg.name}" ให้พนักงาน`} size="max-w-2xl">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">ปี (พ.ศ.)</label>
            <input type="number" className="input" value={year + 543} onChange={(e) => setYear(Number(e.target.value) - 543)} />
          </div>
          <label className="flex items-end gap-2 pb-2 cursor-pointer">
            <input type="checkbox" checked={applyProrate} onChange={(e) => setApplyProrate(e.target.checked)} disabled={!pkg.prorate_on_join} />
            <span className="text-sm">{pkg.prorate_on_join ? 'Prorate ตามวันเริ่มงาน' : '(แพคเกจปิด prorate ไว้)'}</span>
          </label>
        </div>

        <div>
          <input className="input" placeholder="ค้นหา ชื่อ / รหัส / แผนก…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>

        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>เลือกแล้ว {selected.size} / {filtered.length} คน</span>
          <button onClick={toggleAll} className="text-brand-600 hover:underline">
            {selected.size === filtered.length ? 'ล้างที่เลือก' : 'เลือกทั้งหมด'}
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
          {filtered.length === 0 && <div className="p-4 text-center text-sm text-gray-500">ไม่พบพนักงาน</div>}
          {filtered.map((e) => {
            const active = selected.has(e.emp_code);
            const existing = quotaByEmp.get(e.emp_code);
            return (
              <button
                key={e.emp_code}
                onClick={() => toggle(e.emp_code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-gray-100 last:border-0 ${active ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
              >
                <div className={`w-4 h-4 rounded border ${active ? 'bg-brand-600 border-brand-600' : 'border-gray-300'} flex items-center justify-center`}>
                  {active && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.first_name} {e.last_name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {e.emp_code} · {e.department ?? '-'}
                    {existing?.package_name && <> · <span className="text-amber-600">มีแพคเกจ "{existing.package_name}" อยู่</span></>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="btn-secondary flex-1"><X className="w-4 h-4" /> ยกเลิก</button>
          <button onClick={handleApply} disabled={assign.isPending || selected.size === 0} className="btn-primary flex-1">
            <Users className="w-4 h-4" /> {assign.isPending ? 'กำลังใส่…' : `ใส่ให้ ${selected.size} คน`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
